import { Routes } from '@angular/router';

import { tabRoutes } from './routes/tabs.routes';
import { authGuard } from '#guards/auth.guard';
import { memoriesRoutes } from 'src/app/routes/memories.routes';
import { profileRoutes } from './routes/profile.routes';
import { lyfeVaultRoutes } from './routes/lyfe-vault.routes';
import { celebrationRoutes } from './routes/celebration.routes';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'sign-up',
    loadComponent: () => import('./pages/sign-up/sign-up.page').then((m) => m.SignUpPage),
  },
  {
    path: 'sign-up/sign-up-with-email',
    loadComponent: () =>
      import('./pages/sign-up-with-email/sign-up-with-email.page').then(
        (m) => m.SignUpWithEmailPage
      ),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    path: 'update-password',
    loadComponent: () =>
      import('./pages/update-password/update-password.page').then((m) => m.UpdatePasswordPage),
  },
  {
    // DEVELOPMENT ONLY
    path: 'form-development',
    loadComponent: () =>
      import('./pages/test/form-development/form-development.page').then(
        (m) => m.FormDevelopmentPage
      ),
  },
  {
    path: '',
    canActivateChild: [authGuard],
    children: [...tabRoutes],
  },
  {
    path: 'home',
    canActivateChild: [authGuard],
    loadComponent: () => import('#pages/home/home.page').then((m) => m.HomePage),
  },
  {
    path: 'memories',
    canActivateChild: [authGuard],
    children: [...memoriesRoutes],
  },
  {
    path: 'profile',
    canActivateChild: [authGuard],
    children: [...profileRoutes],
  },
  {
    path: 'lyfe-vault',
    canActivateChild: [authGuard],
    children: [...lyfeVaultRoutes],
  },
  {
    path: 'celebrations',
    canActivateChild: [authGuard],
    children: [...celebrationRoutes],
  },
  {
    path: '**',
    redirectTo: '/tabs/home',
  },
];

/**
 * Each tab has its own navigation stack, and you can't navigate from one tab to another.
 * However, you can have pages referenced in more than one tab.
 */
export enum RoutePaths {
  signIn = '/login',
  signUp = '/sign-up',
  signUpWithEmail = '/sign-up/sign-up-with-email',
  forgotPassword = '/forgot-password',
  home = '/tabs/home',
  memories = '/tabs/memories',
  /**
   * When editing a memory, you must pass the memory id as a parameter.
   *
   * Parameters:
   * - id: The id of the memory to edit.
   *
   * State: Memory object.
   */
  memory = '/memories/memory',
  /**
   * When editing a memory, you must pass the memory id as a parameter and replace {id}.
   *
   * Parameters:
   * - id: The id of the memory to edit.
   *
   * State:
   * - memory: Memory object.
   * - memoryAssets: MemoryAssets object.
   */
  assets = '/memories/memory/{id}/assets',
  /**
   * When viewing a video player, you must pass the memory id and asset id as parameters.
   *
   * Parameters:
   * - id: The id of the memory.
   * - assetId: The id of the video to play.
   *
   * State:
   * - signedUrl: The signed URL of the video to play.
   */
  videoPlayer = '/memories/memory/{id}/assets/video/{assetId}',
  lyfeVault = '/tabs/lyfe-vault',
  /**
   * When editing a vault, you must pass the vault id as a parameter.
   *
   * Parameters:
   * - id: The id of the vault to edit.
   */
  vault = '/lyfe-vault/vault',
  celebrations = '/tabs/celebrations',
  /**
   * When editing a celebration, you must pass the celebration id as a parameter.
   *
   * Parameters:
   * - id: The id of the celebration to edit.
   *
   * State:
   * - prompts: An array of CelebrationPrompt to use for selecting prompts.
   * - celebration: Required if editing.
   * - selectedPrompt: An optional CelebrationPrompt that was previously selected.
   */
  celebration = '/celebrations/celebration',
  profile = '/tabs/profile',
  familyDetails = '/profile/family-details',
  /**
   * When editing a family member, you must pass the family member id as a parameter.
   *
   * Parameters:
   * - id: The id of the family member to edit.
   *
   * State: FamilyMember object.
   */
  familyMember = '/profile/family-details/family-member',
  designatedContacts = '/profile/designated-contacts',
  /**
   * When editing a designated contact, you must pass the designated contact id as a parameter.
   *
   * Parameters:
   * - id: The id of the designated contact to edit.
   *
   * State: DesignatedContact object.
   */
  designatedContact = '/profile/designated-contacts/designated-contact',
  changePassword = '/profile/change-password',
  changeEmail = '/profile/change-email',
  deleteAccount = '/profile/delete-account',
}
